import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { AuthService } from '@app/auth/auth.service';
import { Package } from '@models/package';
import { GridDataResult } from '@progress/kendo-angular-grid';
import { State } from '@progress/kendo-data-query';
import { CatalogueUpdatesService } from '@services/catalogueupdates.service';
import { PackagesService } from '@services/packages.service';
import { Observable, Subject } from 'rxjs';
import { map, startWith, takeUntil } from 'rxjs/operators';
import { ConfirmDeleteDialogComponent } from '../../dialogs/confirm-delete/confirm-delete.component';
import { Policies } from '@app/auth/auth-policies';

@Component({
  selector: 'app-catalogue-packages',
  templateUrl: './catalogue-packages.component.html',
  styleUrls: ['./catalogue-packages.component.less'],
})
export class CataloguePackagesComponent implements OnInit, OnDestroy {
  unsub: Subject<void> = new Subject<void>();
  disableGrid = false;
  loading = false;
  editPolicySatisfied = false;
  packages: Package[] = [];
  searchCtrl: FormControl;
  filteredPackages: Observable<Package[]>;
  gridView: GridDataResult;
  gridState: State = {
    sort: [],
    skip: 0,
    take: 10,
    filter: {
      logic: 'and',
      filters: [],
    },
  };

  constructor(
    private packagesService: PackagesService,
    private router: Router,
    private catalogueUpdatesService: CatalogueUpdatesService,
    private deleteDialog: MatDialog,
    private authService: AuthService
  ) {
    this.searchCtrl = new FormControl('');
    this.gridView = {
      data: [],
      total: 0,
    };
  }

  ngOnInit() {
    this.loading = true;
    this.editPolicySatisfied = this.authService.userSatisfiesPolicy(Policies.packagesEdit);

    this.filteredPackages = this.searchCtrl.valueChanges.pipe(
      startWith(''),
      map((pack) => this.filterPackages(pack))
    );

    this.catalogueUpdatesService.catalogueUpdated.pipe(takeUntil(this.unsub)).subscribe(() => {
      this.disableGrid = false;
      if (this.catalogueUpdatesService.refreshRequired) {
        this.catalogueUpdatesService.refreshRequired = false;
        this.refreshData();
      }
    });

    this.refreshData();
  }

  private filterPackages(name: string) {
    let filterResults: Package[] = [];

    if (name !== '') {
      this.gridView = {
        data: this.packages.filter((pack) => pack.name.toLowerCase().includes(name.toLowerCase())),
        total: this.packages.filter((pack) => pack.name.toLowerCase().includes(name.toLowerCase())).length,
      };
      filterResults = this.packages.filter((pack) => pack.name.toLowerCase().includes(name.toLowerCase()));
    } else {
      this.gridView = {
        data: this.packages,
        total: this.packages.length,
      };
      filterResults = [];
    }
    return filterResults;
  }

  private refreshData() {
    this.loading = true;
    this.packages = [];
    this.packagesService.getPackages().subscribe((packages) => {
      this.packages = packages;
      this.loadItems();
      this.loading = false;
    });
  }

  private loadItems() {
    this.gridView = {
      data: this.packages,
      total: this.packages.length,
    };
    // filter the products and services dropdowns based on their associated package
    this.gridView.data.forEach(function (element) {});
  }

  addHandler() {
    this.disableGrid = true;
    this.router.navigate(['/management/catalogue/packages', { outlets: { 'action-panel': ['edit-pkg', '_'] } }]);
  }

  editHandler({ sender, rowIndex, dataItem }: { sender: any; rowIndex: number; dataItem: Package }) {
    this.disableGrid = true;
    this.router.navigate([
      '/management/catalogue/packages',
      { outlets: { 'action-panel': ['edit-pkg', dataItem.id] } },
    ]);
  }

  removeHandler({ dataItem }: { dataItem: Package }) {
    const dialogRef = this.deleteDialog.open(ConfirmDeleteDialogComponent, {
      width: '250px',
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.unsub))
      .subscribe((result) => {
        if (result === 'delete') {
          this.packagesService.removePackage(dataItem.id).subscribe(() => {
            this.refreshData();
          });
        }
      });
  }

  ngOnDestroy() {
    this.unsub.next();
    this.unsub.complete();
  }
}
